<template>
  <product-section-card
    class="recommended-menu"
    :title="$t('branch_detail.menu.title')"
    :subtitle="$t('branch_detail.menu.condition')"
    :is-view-all-shown="menuList.length > DISPLAY_MENU_COUNT"
    @view-all-click="onViewAllClick"
  >
    <template #title-action>
      <bottom-drawer-select v-model="discount" container-class="-mr-3" :title="$t('branch_detail.menu.discount')" v-bind="menuSelectorProps" />
    </template>
    <product-section-menu-list :menu-list="displayMenu" />
    <template v-if="menuList.length <= DISPLAY_MENU_COUNT">
      <BulletedList class="text-sm text-gray-80" :list="noticeStrings" />
    </template>
  </product-section-card>

  <content-viewer
    v-model:show="isViewerShown"
    :title="$t('branch_detail.menu.title')"
    :is-full-height="true"
    :is-footer-shown="false"
    :with-trigger-btn="false"
    main-content-class="py-0"
  >
    <div class="flex sticky top-0 bg-white justify-between items-center gap-2 py-3">
      <p class="text-base">{{ $t('branch_detail.menu.title') }}</p>
      <etg-select v-model="discount" class="-mr-3" v-bind="menuSelectorProps" />
    </div>
    <product-section-menu-list :menu-list="menuList" />
    <BulletedList class="text-sm text-gray-80" :list="noticeStrings" />
  </content-viewer>
</template>

<script setup lang="ts">
import ProductSectionCard from '@/components/product/product-detail/product-section-card.vue'
import ProductSectionMenuList from '@/components/product/product-detail/product-section-menu-list.vue'
import BottomDrawerSelect from '@/components/common/bottom-drawer-select.vue'
import ContentViewer from '@/components/common/content-viewer.vue'
import BulletedList from '@/components/common/bulleted-list.vue'

import type { BranchMenuItemsRes } from '@/types/api'

interface Props {
  branchMenuItem: BranchMenuItemsRes['data']
  selectedDiscount?: number
}

const DISPLAY_MENU_COUNT = 4

const { t } = useI18n()
const props = defineProps<Props>()
const { isSoldOut } = useBranchDetailContext()
const { selectedDiscount } = toRefs(props)

const branchMenuItem = computed(() => props.branchMenuItem || {})
const discounts = computed(() =>
  Object.keys(branchMenuItem.value)
    .map(Number)
    .sort((a, b) => b - a)
)
const defaultDiscount = computed(() => discounts.value[0])

const isViewerShown = ref(false)
const discount = ref(selectedDiscount.value || defaultDiscount.value)

const selectOptions = computed(() => discounts.value.map(discount => ({ label: `-${discount} %`, value: discount })))
const menuList = computed(() => branchMenuItem.value[discount.value] || [])
const displayMenu = computed(() => menuList.value.slice(0, DISPLAY_MENU_COUNT))
const menuSelectorProps = computed(() => ({
  consistentMenuWidth: false,
  options: selectOptions.value,
  placement: 'bottom-end',
  size: 'small' as const,
  type: 'primary-text' as const,
  menuProps: { class: 'w-60' },
  disabled: isSoldOut.value,
}))
const noticeStrings = computed(() => t('branch_detail.menu.notices').split('\n'))

watch(selectedDiscount, newSelectedDiscount => {
  if (newSelectedDiscount) {
    discount.value = newSelectedDiscount
  }
})

function onViewAllClick() {
  isViewerShown.value = true
}
</script>
